import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Info, ArrowLeft, Hourglass, Check } from "@phosphor-icons/react";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";

export default function ApplyReporterPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    real_name: "",
    location: "",
    affiliation: "",
    qualifications: "",
    references: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (user === null) {
    return <div className="p-8 text-center text-tac-dim">Loading…</div>;
  }
  if (user === false) {
    navigate("/login?next=/apply-reporter", { replace: true });
    return null;
  }

  const existing = user.reporter_application;
  const alreadyVerified = !!user.verified;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.real_name.trim().length < 2) return setError("Please enter your real name.");
    if (form.location.trim().length < 2) return setError("Please enter where you are based.");
    if (form.qualifications.trim().length < 10)
      return setError("Tell us a bit more about your qualifications (10+ chars).");

    setBusy(true);
    try {
      await api.post("/auth/apply-reporter", form);
      await refresh?.();
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit application.");
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!window.confirm("Withdraw your application?")) return;
    setBusy(true);
    try {
      await api.delete("/auth/apply-reporter");
      await refresh?.();
      setSuccess(false);
      setForm({ real_name: "", location: "", affiliation: "", qualifications: "", references: "" });
    } catch (err) {
      setError("Could not withdraw.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="apply-reporter-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-primary mb-4"
      >
        <ArrowLeft size={12} weight="bold" /> Back
      </Link>

      <div className="mb-6">
        <h1 className="font-display font-black text-3xl text-tac-text flex items-center gap-2">
          <ShieldCheck size={24} weight="duotone" className="text-tac-primary" />
          Apply to be a Verified Reporter
        </h1>
        <p className="mt-2 text-[15px] text-tac-dim leading-relaxed">
          Verified reporters get a gold shield on their field updates and are
          trusted by the community as a reliable on-scene source. We hand them
          out to people with verifiable ties to fire ops, emergency
          management, scanner monitoring, ham radio, or simply long-time
          residents with strong situational awareness.
        </p>
      </div>

      {alreadyVerified ? (
        <div className="border border-tac-primary/55 bg-tac-primary/10 rounded-sm p-4 flex items-start gap-3" data-testid="already-verified">
          <ShieldCheck size={20} weight="fill" className="text-tac-primary shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-sm text-tac-text">You're already verified.</div>
            <div className="text-[15px] text-tac-dim mt-1">
              Your field updates on the incident pages already display the gold Verified badge.
            </div>
          </div>
        </div>
      ) : existing && existing.status === "pending" && !success ? (
        <div className="space-y-4">
          <div className="border border-tac-warning/55 bg-tac-warning/10 rounded-sm p-4 flex items-start gap-3" data-testid="application-pending">
            <Hourglass size={20} weight="fill" className="text-tac-warning shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm text-tac-text">Application under review</div>
              <div className="text-[15px] text-tac-dim mt-1">
                Submitted {existing.applied_at ? new Date(existing.applied_at).toLocaleDateString() : "recently"}.
                We aim to review within 7 days.
              </div>
            </div>
          </div>
          <ApplicationSummary data={existing} />
          <button
            onClick={withdraw}
            disabled={busy}
            data-testid="withdraw-application"
            className="text-[11px] font-bold uppercase tracking-wider px-3 py-2 border border-tac-border bg-tac-surface hover:bg-tac-surface2 text-tac-dim rounded-sm disabled:opacity-40"
          >
            Withdraw application
          </button>
        </div>
      ) : success ? (
        <div className="border border-tac-safe/55 bg-tac-safe/10 rounded-sm p-4 flex items-start gap-3" data-testid="application-success">
          <Check size={20} weight="bold" className="text-tac-safe shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-sm text-tac-text">Application submitted.</div>
            <div className="text-[15px] text-tac-dim mt-1">
              We'll review and let you know within 7 days. You can keep posting
              regular (unverified) field updates in the meantime.
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="apply-form">
          {!user.email_verified && (
            <div className="border border-tac-warning/55 bg-tac-warning/10 rounded-sm p-3 flex items-start gap-2 text-[13px] text-tac-text">
              <Info size={14} weight="fill" className="text-tac-warning shrink-0 mt-0.5" />
              Please verify your email address before applying.
            </div>
          )}

          <Field label="Real name *" testid="apply-real-name">
            <input
              type="text"
              required
              value={form.real_name}
              onChange={(e) => update("real_name", e.target.value)}
              placeholder="Jane Doe"
              className="apply-input"
            />
          </Field>

          <Field label="Where are you based? *" testid="apply-location">
            <input
              type="text"
              required
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              placeholder="e.g., Tonasket, Okanogan County"
              className="apply-input"
            />
          </Field>

          <Field label="Affiliation (optional)" testid="apply-affiliation" hint="Fire department, ham club, search & rescue, EM agency, etc.">
            <input
              type="text"
              value={form.affiliation}
              onChange={(e) => update("affiliation", e.target.value)}
              placeholder="e.g., Okanogan Co. Fire District 5 / N7XYZ"
              className="apply-input"
            />
          </Field>

          <Field label="Qualifications & background *" testid="apply-qualifications" hint="Tell us why we should trust your reporting. Specific is better.">
            <textarea
              required
              minLength={10}
              maxLength={2000}
              rows={5}
              value={form.qualifications}
              onChange={(e) => update("qualifications", e.target.value)}
              placeholder="e.g., Retired wildland firefighter (20 years FS R6). Monitor Okanogan County FD scanner daily. Live 4 miles north of Tonasket."
              className="apply-input resize-y"
            />
          </Field>

          <Field label="References (optional)" testid="apply-references" hint="Names/contacts of people who can vouch for you. We may reach out.">
            <input
              type="text"
              value={form.references}
              onChange={(e) => update("references", e.target.value)}
              placeholder="e.g., Chief Smith, Okanogan FD — 509-555-0100"
              className="apply-input"
            />
          </Field>

          {error && (
            <div className="text-[13px] text-tac-danger border border-tac-danger/40 bg-tac-danger/10 rounded-sm px-3 py-2" data-testid="apply-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !user.email_verified}
            data-testid="apply-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-[12px] font-bold uppercase tracking-wider bg-tac-primary text-white rounded-sm hover:bg-tac-primary/90 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition"
          >
            <ShieldCheck size={14} weight="fill" /> {busy ? "Submitting…" : "Submit application"}
          </button>
          <p className="text-[12px] text-tac-faint leading-relaxed">
            By applying you agree your real name and location will be shared with
            site admins for review. Your name is never displayed publicly — your
            display name is what appears on field updates.
          </p>
        </form>
      )}

      <style>{`
        .apply-input {
          width: 100%;
          background: var(--tac-surface, #111614);
          border: 1px solid #232B27;
          color: #E8EDE9;
          padding: 10px 12px;
          font-size: 14px;
          border-radius: 2px;
          outline: none;
          transition: border-color 0.15s;
        }
        .apply-input::placeholder { color: #5C6963; }
        .apply-input:focus { border-color: #F2542D; }
      `}</style>
    </div>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-wider font-bold text-tac-text block mb-1">
        {label}
      </span>
      {children}
      {hint && <span className="text-[12px] text-tac-faint block mt-1">{hint}</span>}
    </label>
  );
}

function ApplicationSummary({ data }) {
  return (
    <div className="border border-tac-border bg-tac-surface/40 rounded-sm p-4 space-y-2 text-[13px]">
      <Row label="Name" value={data.real_name} />
      <Row label="Location" value={data.location} />
      {data.affiliation && <Row label="Affiliation" value={data.affiliation} />}
      <Row label="Qualifications" value={data.qualifications} multiline />
      {data.references && <Row label="References" value={data.references} />}
    </div>
  );
}

function Row({ label, value, multiline }) {
  return (
    <div className={multiline ? "" : "flex gap-2"}>
      <span className="text-[10px] uppercase tracking-wider font-bold text-tac-faint shrink-0">{label}:</span>
      <span className={`text-tac-text ${multiline ? "block mt-1 whitespace-pre-line" : ""}`}>{value}</span>
    </div>
  );
}
